import React, { useCallback, useRef, useState } from "react";

import { Info, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { SnipResultPopover } from "@/components/FilePreview/SnipResultPopover";
import { MapToReportBuilderDialog } from "@/components/MapToReportBuilderDialog";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as DiamondsIcon } from "@assets/svg/diamonds.svg";
import { ReactComponent as EditIcon } from "@assets/svg/document-with-pen.svg";
import { ReactComponent as RegenIcon } from "@assets/svg/generate-blue.svg";

import { Button } from "@components/ui/button";
import { Textarea } from "@components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import {
  useRetrySummaryGeneration,
  useUpdateDocumentReviewStatus,
  useUpdateFileSummary,
} from "@hooks/mutations/use-files";

import { setMessage } from "@redux/notification";

type SummaryPanelProps = {
  file: DocSorterFileSchema | null;
  isVisible: boolean;
  onClose: () => void;
  onSaveNote?: (text: string) => void;
};

export const SummaryPanel: React.FC<SummaryPanelProps> = ({
  file,
  isVisible,
  onClose,
  onSaveNote,
}) => {
  const [draft, setDraft] = useState<{ fileId: string; text: string } | null>(null);
  const isEditing = !!file && draft?.fileId === file.id;
  const summary = file?.full_summary || "";
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [mapText, setMapText] = useState<string | null>(null);
  const [isAcceptFlow, setIsAcceptFlow] = useState(false);

  const dispatch = useDispatch();
  const updateFileSummary = useUpdateFileSummary();
  const updateDocumentReviewStatus = useUpdateDocumentReviewStatus();
  const regenerateFile = useRetrySummaryGeneration();

  const [isRegenConfirmOpen, setIsRegenConfirmOpen] = useState(false);
  // MDRS-532: regenerating mid-edit hides the draft behind "Regenerating…",
  // and saving it afterwards overwrites the new summary. Lock Regenerate
  // until the edit is saved or cancelled.
  const isRegenerateBlocked = isEditing || updateFileSummary.isPending;
  const regenerateBlockedReason = isEditing
    ? "Save or cancel your edits to regenerate"
    : "Wait for the summary to finish saving";
  // The modal has no trigger of its own, so focus comes back here on close.
  const regenerateButtonRef = useRef<HTMLButtonElement>(null);

  // Fired from the ConfirmationModal's Confirm action — the modal owns
  // the user-facing yes/no decision so we never reach for the
  // browser-native window.confirm (jarring, unstyled, untestable).
  const handleConfirmRegenerate = () => {
    if (!file) return;
    regenerateFile.mutate(file.id, {
      onSuccess: () => {
        dispatch(
          setMessage({
            content: "Summary regeneration started successfully",
            type: "success",
          })
        );
        setIsRegenConfirmOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to regenerate summary";
        dispatch(
          setMessage({
            content: errorMessage,
            type: "error",
          })
        );
      },
    });
  };

  const startEditing = () => {
    if (!file) return;
    setDraft({ fileId: file.id, text: summary });
  };

  const handleSave = async () => {
    if (!file || !isEditing || !draft.text.trim()) return;
    const fileId = file.id;

    updateFileSummary.mutate(
      {
        fileId,
        summary: { full_summary: draft.text },
      },
      {
        onSuccess: () => {
          setDraft((current) => (current?.fileId === fileId ? null : current));
          dispatch(
            setMessage({
              content: "Document summary updated",
              type: "success",
            })
          );
        },
        onError: (_error) => {
          dispatch(
            setMessage({
              content: "Failed to update document summary",
              type: "error",
            })
          );
        },
      }
    );
  };

  const handleSummaryMouseUp = useCallback(() => {
    if (isEditing) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text) {
      setSelectedText(text);
    }
  }, [isEditing]);

  const handleSelectionClose = useCallback(() => {
    setSelectedText(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleMapToReportBuilder = useCallback(() => {
    if (!selectedText) return;
    setMapText(selectedText);
    setSelectedText(null);
    window.getSelection()?.removeAllRanges();
  }, [selectedText]);

  const handleCancelEdit = () => {
    setDraft(null);
  };

  const handleStartAcceptFlow = () => {
    if (!file || !file.case?.id) {
      dispatch(
        setMessage({
          content: "No case information available",
          type: "error",
        })
      );
      return;
    }
    if (!summary) return;
    setIsAcceptFlow(true);
    setMapText(summary);
  };

  const handleAcceptMapped = async () => {
    if (!file || !file.case?.id) return;

    try {
      await updateDocumentReviewStatus.mutateAsync({
        fileId: file.id,
        reviewStatus: { is_document_reviewed: true },
      });

      dispatch(
        setMessage({
          content: "File accepted and marked as reviewed",
          type: "success",
        })
      );

      onClose();
    } catch (_error) {
      dispatch(
        setMessage({
          content: "Failed to accept file",
          type: "error",
        })
      );
    }
  };

  if (!isVisible) {
    return null;
  }

  const regenerateButton = (
    <Button
      ref={regenerateButtonRef}
      variant="secondary-subtle"
      size="sm"
      className="h-9"
      onClick={() => setIsRegenConfirmOpen(true)}
      disabled={isRegenerateBlocked}
    >
      <RegenIcon className="size-3 [&_path]:fill-secondary" />
      Regenerate
    </Button>
  );

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-4 flex flex-shrink-0 items-center justify-between">
        <div className="relative w-full">
          <h2 className="mb-2 flex items-center gap-1.5 text-base font-semibold">
            <DiamondsIcon className="size-4" />
            Summary
          </h2>
          <div className="flex items-start gap-1.5 text-sm text-foreground-muted">
            <Info className="mt-0.5 size-4 flex-shrink-0" />
            <p>
              Always review and verify the summary against the original source
              document before using it. Edit or replace the summary as needed. Only
              select Map to Report Builder once you are satisfied that the content is
              accurate and supported by the source material.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute right-[-10px] top-[-10px] h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-shrink-0 flex-row-reverse items-center space-x-2">
        <div className="flex items-center gap-2">
          {file && (
            <>
              {!isEditing && (
                <Button
                  variant="simple-outline"
                  size="sm"
                  className="h-9"
                  onClick={startEditing}
                >
                  <EditIcon className="size-4 [&_path]:fill-foreground" />
                  Edit
                </Button>
              )}

              {isRegenerateBlocked ? (
                // A disabled button gets no pointer events or focus, so the
                // tooltip sits on a focusable wrapper (as in HeaderActions).
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <span tabIndex={0} className="inline-flex">
                      {regenerateButton}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {regenerateBlockedReason}
                  </TooltipContent>
                </Tooltip>
              ) : (
                regenerateButton
              )}

              <ConfirmationModal
                type="info"
                icon={<RegenIcon className="size-12 [&_path]:fill-primary" />}
                title="Regenerate Summary?"
                description={`Are you sure you want to regenerate the summary for "${stripBundlePrefix(file.name)}"? This will replace the current summary with a new one.`}
                confirmButtonText="Yes, Regenerate"
                cancelButtonText="Cancel"
                onConfirm={handleConfirmRegenerate}
                isLoading={regenerateFile.isPending}
                open={isRegenConfirmOpen}
                onOpenChange={setIsRegenConfirmOpen}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  regenerateButtonRef.current?.focus();
                }}
              />
            </>
          )}
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-auto rounded-md border bg-gray-50 p-4"
        onMouseUp={handleSummaryMouseUp}
      >
        {!file ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground text-sm">
              No document selected
            </span>
          </div>
        ) : file.status === "PROCESSING" ? (
          // The Azure summary function is generating a new summary right
          // now. Without this affordance the doctor sees the stale text
          // and assumes Regenerate did nothing — the originating
          // complaint for STP-92.
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground animate-pulse text-sm">
              Regenerating summary…
            </span>
          </div>
        ) : !summary && !isEditing ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground text-sm">
              No summary available for this document.
            </span>
          </div>
        ) : isEditing ? (
          <Textarea
            placeholder="Write summary here"
            value={draft.text}
            onChange={(e) => setDraft({ fileId: file.id, text: e.target.value })}
            className="h-full w-full resize-none border-none bg-transparent p-0 text-sm leading-relaxed shadow-none [field-sizing:fixed] focus-visible:ring-0"
            disabled={updateFileSummary.isPending}
          />
        ) : (
          <div
            className="whitespace-pre-line break-words text-sm"
            onDoubleClick={startEditing}
          >
            {summary}
          </div>
        )}
      </div>

      {selectedText && (
        <SnipResultPopover
          title="Selected Text"
          text={selectedText}
          isLoading={false}
          isProcessing={false}
          error={null}
          onClose={handleSelectionClose}
          onSaveNote={() => onSaveNote?.(selectedText)}
          onMapToReportBuilder={handleMapToReportBuilder}
        />
      )}

      <div className="my-4 flex flex-shrink-0 justify-end space-x-2">
        {!isEditing && (
          <Button
            size="sm"
            onClick={handleStartAcceptFlow}
            disabled={!file || !summary}
          >
            Map to Report Builder
          </Button>
        )}

        {isEditing && (
          <div className="mb-2 flex justify-end space-x-2">
            <Button
              size="sm"
              onClick={handleSave}
              // The API ignores an empty summary, so don't offer to save one.
              disabled={updateFileSummary.isPending || !draft?.text.trim()}
              isLoading={updateFileSummary.isPending}
            >
              {updateFileSummary.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="simple-outline"
              size="sm"
              onClick={handleCancelEdit}
              disabled={updateFileSummary.isPending}
            >
              Cancel
            </Button>
          </div>
        )}

        <MapToReportBuilderDialog
          open={!!mapText}
          onOpenChange={(open) => {
            if (!open) {
              setMapText(null);
              setIsAcceptFlow(false);
            }
          }}
          text={mapText ?? ""}
          targetField="summary_from_doc_sorter"
          extractType="summary_highlight"
          caseNumber={file?.case?.case_number}
          templateId={file?.case?.template?.id}
          sourceFileName={file?.name}
          onMapped={isAcceptFlow ? handleAcceptMapped : undefined}
        />
      </div>
    </div>
  );
};
